/**
 * Angular helper utilities for working with HTTP query strings.
 *
 * The main goal is to convert a plain JavaScript object (`QueryParams`) into
 * a `HttpParams` instance that:
 *
 * 1. Keeps square brackets in array keys (`foo[]`) untouched – Angular
 *    normally percent‑encodes them.
 * 2. Flattens array values so that each value appears as a separate
 *    key/value pair (`foo[]=a&foo[]=b`).
 *
 * These helpers are especially handy when you need to build a request
 * such as:
 *
 *   GET /api/users?role[]=admin&role[]=user&active=true
 *
 * Instead of manually stringifying the query string, you can write:
 *
 *   const params = QueryUtils.transformParams({
 *     role: ['admin', 'user'],
 *     active: true
 *   });
 *
 * and pass `params` to `HttpClient.get<…>(url, { params })`.
 */

import {HttpParams, HttpUrlEncodingCodec} from '@angular/common/http';

/**
 * The only types that can be represented in a URL‑encoded query string.
 *
 * - `string`, `number`, `boolean` are converted via `String(value)`.
 * - `string[]`, `number[]`, `boolean[]` are treated as *multiple* query
 *   parameters with the same key (e.g. `foo[]=a&foo[]=b`).
 * - `undefined` is converted to an empty string (`''`), which results in
 *   a key with no value (`foo=`). This mirrors the behaviour of
 *   Angular’s `HttpParams` when you call `.append(key, undefined)`.
 */
export type AllowedQueryParamTypes = string | number | boolean | string[] | number[] | boolean[] | undefined;

/**
 * Simple alias for a map of query‑parameter names to values.
 */
export type QueryParams = Record<string, AllowedQueryParamTypes>;

/**
 * Custom `HttpUrlEncodingCodec` that leaves square brackets intact.
 *
 * Angular’s default encoder percent‑encodes `[` and `]`. For
 * REST‑ful APIs that expect array syntax (`foo[]`) we need to avoid
 * that encoding, otherwise the server would see `%5B` instead of
 * `[`.
 */
export class NoBracketEncoder extends HttpUrlEncodingCodec {
    /**
     * Override the key‑encoding behaviour.
     *
     * @param key The key to encode.
     * @returns The original key unchanged.
     */
    override encodeKey(key: string): string {
        // Do *not* encode the square brackets.
        return key;
    }
}

/**
 * Utility class that transforms plain objects into `HttpParams`.
 *
 * All public methods are static – there is no need to instantiate the
 * class.  The helpers preserve immutability by returning a **new**
 * `HttpParams` instance each time.
 */
export abstract class QueryUtils {
    /**
     * Convert a `QueryParams` object into a `HttpParams` instance.
     *
     * * The returned instance uses {@link NoBracketEncoder} so that array
     *   keys (`foo[]`) stay readable.
     * * Arrays are *flattened* – every element becomes its own
     *   query pair.  For example: `{ role: ['admin', 'user'] }`
     *   becomes `role[]=admin&role[]=user`.
     * * If the input is `undefined` or empty, an empty `HttpParams`
     *   instance is returned.
     *
     * @param params The plain object to transform.
     * @returns A new `HttpParams` instance ready to be used with
     *          `HttpClient`.
     */
    public static transformParams(params?: QueryParams): HttpParams {
        const result = new HttpParams({encoder: new NoBracketEncoder()});
        if (!params) {
            return result;
        }
        return QueryUtils.appendParams(result, params);
    }

    /**
     * Append (or prepend) parameters to an existing `HttpParams` instance.
     *
     * The method is intentionally *pure* – it never mutates the
     * `existing` instance.  Instead it returns a brand‑new instance that
     * contains all of the original parameters plus the new ones.
     *
     * @param existing Existing `HttpParams` to which values will be added.
     * @param params   New parameters to add.
     * @returns A new `HttpParams` instance that includes both the
     *          original and the new parameters.
     */
    public static appendParams(existing: HttpParams, params?: QueryParams): HttpParams {
        if (!params) {
            return existing;
        }

        for (const key of Object.keys(params)) {
            const value = params[key];

            if (Array.isArray(value)) {
                // For arrays we need multiple key/value pairs.
                // e.g. key = 'role', value = ['admin', 'user']
                // => role[]=admin&role[]=user
                value.forEach((v) => {
                    existing = existing.append(`${key}[]`, v);
                });
            } else {
                // Primitive or undefined – convert to string or empty string.
                existing = existing.append(key, value === undefined ? '' : String(value));
            }
        }
        return existing;
    }
}
